import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import NanoPulseLogo from "../assets/NanoPulseLogo.png";
import { isAdminLoggedIn, storeAdminToken } from "../../services/localStorage";
import { login, forgotPassword } from "../../services/authService";
import { toast } from "react-toastify";

const LoginModal = ({ onClose }) => {

    const navigate = useNavigate();

    const [isSignup] = useState(false);
    const [isForgot, setIsForgot] = useState(false);

    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        email: "",
        password: "",
        confirmPassword: ""
    });

    const [errors, setErrors] = useState({});

    // Already logged in
    if (isAdminLoggedIn()) {
        return <Navigate to="/admin" replace />;
    }

    // Handle Input
    const handleChange = (e) => {

        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value
        });

        setErrors({
            ...errors,
            [name]: ""
        });
    };

    // Validation
    const validateForm = () => {

        let isValid = true;
        const newErrors = {};

        // Email
        if (!formData.email) {

            newErrors.email = "Email is required";
            isValid = false;

        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {

            newErrors.email = "Please enter a valid email";
            isValid = false;
        }

        // Password only for login
        if (!isForgot && !formData.password) {

            newErrors.password = "Password is required";
            isValid = false;
        }

        setErrors(newErrors);

        return isValid;
    };

    // Submit
    const handleSubmit = async (e) => {

        e.preventDefault();

        if (!validateForm()) return;

        // ================= FORGOT PASSWORD =================
        if (isForgot) {

            try {

                setLoading(true);

                const data = await forgotPassword({
                    email: formData.email,
                });

                if (data.status === 200) {

                    toast.success(
                        data.message ||
                        "Password reset link sent successfully"
                    );

                    setIsForgot(false);

                } else {

                    toast.error(
                        data.message || "Something went wrong"
                    );
                }

            } catch (error) {

                toast.error(
                    error.message ||
                    "Failed to send reset link"
                );

            } finally {

                setLoading(false);
            }

            return;
        }

        // ================= LOGIN =================

        try {

            const data = await login({
                email: formData.email,
                password: formData.password,
            });

            if (data.status === 200) {

                toast.success("Login successful");

                // Store token
                storeAdminToken(data.token);

                // Role Handling
                if (data.role === "Admin") {

                    navigate("/admin", { replace: true });

                } else if (data.role === "User") {

                    if (data.redirect_url) {

                        window.location.href = data.redirect_url;

                    } else {

                        toast.error("Redirect URL missing");
                    }

                } else {

                    toast.error("Unknown role");
                }

            } else {

                toast.error(
                    data.message || "Login failed"
                );
            }

        } catch (error) {

            console.error(error);

            toast.error(
                error.message ||
                "Invalid email or password"
            );
        }
    };

    // Close Handler
    const handleClose = () => {

        if (onClose) {

            onClose();

        } else {

            navigate("/");
        }
    };

    return (

        <div style={styles.overlay}>

            <div style={styles.card}>

                {/* Close Button */}
                <span
                    style={styles.closeBtn}
                    onClick={handleClose}
                >
                    ✕
                </span>

                {/* Logo */}
                <img
                    src={NanoPulseLogo}
                    alt="logo"
                    style={styles.logo}
                />

                <form onSubmit={handleSubmit}>

                    {/* Email */}
                    <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={formData.email}
                        onChange={handleChange}
                        style={{
                            ...styles.input,
                            border: errors.email
                                ? "1px solid red"
                                : "1px solid #ccc"
                        }}
                    />

                    {errors.email && (
                        <p style={styles.error}>
                            {errors.email}
                        </p>
                    )}

                    {/* Password */}
                    {!isForgot && (
                        <>
                            <input
                                type="password"
                                name="password"
                                placeholder="Password"
                                value={formData.password}
                                onChange={handleChange}
                                style={{
                                    ...styles.input,
                                    border: errors.password
                                        ? "1px solid red"
                                        : "1px solid #ccc"
                                }}
                            />

                            {errors.password && (
                                <p style={styles.error}>
                                    {errors.password}
                                </p>
                            )}
                        </>
                    )}

                    {/* Forgot Password */}
                    {!isSignup && !isForgot && (

                        <p
                            style={styles.link}
                            onClick={() => {

                                setIsForgot(true);
                                setErrors({});
                            }}
                        >
                            Forgot Password?
                        </p>
                    )}

                    {/* Back to Login */}
                    {isForgot && (

                        <p
                            style={styles.link}
                            onClick={() => {

                                setIsForgot(false);
                                setErrors({});
                            }}
                        >
                            Back to Login
                        </p>
                    )}

                    {/* Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            ...styles.loginBtn,
                            opacity: loading ? 0.9 : 1,
                            cursor: loading ? "not-allowed" : "pointer",
                        }}
                    >
                        {loading ? (
                            <div style={styles.loaderWrapper}>
                                <div style={styles.loader}></div>
                                <span>Sending...</span>
                            </div>
                        ) : isForgot ? (
                            "Send Reset Link"
                        ) : (
                            "Login"
                        )}
                    </button>

                </form>

            </div>

        </div>
    );
};

export default LoginModal;


/* ================= STYLES ================= */

const styles = {

    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 999
    },

    card: {
        background: "#fff",
        padding: "40px 30px",
        width: "360px",
        borderRadius: "12px",
        textAlign: "center",
        position: "relative",
        boxShadow: "0 10px 30px rgba(0,0,0,0.2)"
    },

    closeBtn: {
        position: "absolute",
        top: "10px",
        right: "15px",
        cursor: "pointer",
        fontSize: "18px",
        color: "#666"
    },

    logo: {
        width: "200px",
        marginBottom: "25px"
    },

    input: {
        width: "100%",
        padding: "12px",
        marginBottom: "10px",
        borderRadius: "6px",
        outline: "none"
    },

    loginBtn: {
        width: "90%",
        padding: "10px",
        background: "#2f66b1",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontWeight: "500",
        marginTop: "10px"
    },

    error: {
        color: "red",
        fontSize: "0.8rem",
        textAlign: "left",
        marginBottom: "5px"
    },

    link: {
        color: "#2f66b1",
        cursor: "pointer",
        fontSize: "0.85rem",
        textAlign: "right",
        display: "block",
        marginBottom: "10px"
    },
    loaderWrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
    },

    loader: {
        width: "18px",
        height: "18px",
        border: "3px solid rgba(255,255,255,0.4)",
        borderTop: "3px solid #fff",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
    },
};